import React, { useContext, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';

import {
  LayoutDashboard, ClipboardList, FileText, Users, BookOpen, Settings, LogOut,
  ChevronLeft, ChevronRight, ChevronDown, HelpCircle, ListChecks, ScrollText, Mail,
  MailCheck, ShieldCheck, Send, CheckSquare, Palette, Shield,
} from 'lucide-react';

// perms: null = all authenticated users; array = show if user has ANY listed permission
// superOnly: true = visible only to super admins (hidden from all role-based users)
const NAV_TOP = [
  { to: '/admin/dashboard',    label: 'Overview',       icon: LayoutDashboard, perms: null },
  { to: '/admin/intakes',      label: 'Intake Periods', icon: ClipboardList,   perms: ['intakes.view'] },
  { to: '/admin/applications', label: 'Applications',   icon: FileText,        perms: ['applications.view'] },
  { to: '/admin/students',     label: 'Students',       icon: Users,           perms: ['students.view'], superOnly: true },
  { to: '/admin/cohorts',      label: 'Cohorts',        icon: BookOpen,        perms: ['academy.manage', 'students.view', 'applications.view'] },
  { to: '/admin/bulk-email',   label: 'Bulk Email',     icon: Send,            perms: ['applications.view'] },
  { to: '/admin/email-logs',   label: 'Email Logs',     icon: MailCheck,       perms: ['applications.view'] },
  { to: '/admin/audit-logs',   label: 'Audit Logs',     icon: ShieldCheck,     perms: ['users.view'] },
  { to: '/admin/users',        label: 'Users',          icon: Users,           perms: ['users.view'] },
  { to: '/admin/roles',        label: 'Roles',          icon: Shield,          perms: ['roles.view'] },
  { to: '/admin/guide',        label: 'Admin Guide',    icon: HelpCircle,      perms: null, superOnly: true },
];

const SETTINGS_ITEMS = [
  { tab: 'content',      label: 'Landing Page',       icon: FileText },
  { tab: 'faqs',         label: 'FAQs',               icon: HelpCircle },
  { tab: 'eligibility',    label: 'Eligibility',          icon: ListChecks },
  { tab: 'aptitude-test',  label: 'Assessment Config', icon: Settings },
  { tab: 'partnerships',   label: 'Partnerships',         icon: Users },
  { tab: 'declarations',   label: 'Declarations',         icon: ListChecks },
  { tab: 'terms',        label: 'Terms & Conditions', icon: ScrollText },
  { tab: 'admissions',   label: 'Admissions',         icon: Settings },
  { tab: 'questions',    label: 'Question Bank',      icon: ClipboardList },
  { tab: 'emails',       label: 'Email Templates',    icon: Mail },
  { tab: 'checklist',    label: 'Review Checklist',   icon: CheckSquare },
  { tab: 'scoring',      label: 'Scoring Weights',    icon: Settings },
  { tab: 'branding',     label: 'Branding',           icon: Palette },
  { tab: 'system',       label: 'System Config',      icon: Settings },
  { tab: 'devtools',     label: 'Dev Tools',          icon: Settings },
];

export default function AdminLayout() {
  const { user, logout, hasPermission } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const canSee = (perms, superOnly = false) => {
    if (user?.is_super_admin) return true;
    if (superOnly) return false;
    if (perms === null) return true;
    return perms.some(p => hasPermission(p));
  };

  const onSettingsRoute = location.pathname.startsWith('/admin/settings');

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('adminSidebarCollapsed') === 'true'
  );
  const [settingsOpen, setSettingsOpen] = useState(onSettingsRoute);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('adminSidebarCollapsed', String(next));
  };

  const sidebarW = collapsed ? 'w-16' : 'w-60';
  const mainML  = collapsed ? 'ml-16' : 'ml-60';

  // Shared class builder for top-level nav items
  const navCls = (isActive) =>
    `flex items-center transition-all duration-150
    ${collapsed
      ? `justify-center mx-2 px-0 py-2.5 rounded-lg ${isActive ? 'bg-white/15 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'}`
      : `gap-3 mx-3 py-2.5 rounded-lg text-sm border-l-[3px] rounded-l-none pl-[13px]
        ${isActive
          ? 'bg-white/15 text-white font-semibold border-secondary'
          : 'text-white/60 hover:text-white hover:bg-white/10 border-transparent'
        }`
    }`;

  return (
    <div className="flex min-h-screen">
      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      <aside className={`${sidebarW} bg-primary text-white flex flex-col fixed inset-y-0 left-0 z-40 shadow-xl transition-all duration-200 overflow-hidden`}>

        {/* Branding */}
        <div className={`border-b border-white/10 flex items-center justify-between ${collapsed ? 'px-0 py-4 justify-center' : 'px-5 py-5'}`}>
          {!collapsed && (
            <div>
              <p className="text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1.5">Admin Portal</p>
              <h1 className="font-playfair font-bold text-base leading-snug text-white">
                FB Attorneys<br />Academy
              </h1>
            </div>
          )}
          <button
            onClick={toggleCollapsed}
            className={`flex-shrink-0 rounded-md p-1.5 text-white/50 hover:text-white hover:bg-white/10 transition-colors ${collapsed ? 'mx-auto' : ''}`}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden space-y-0.5">

          {/* Top nav items */}
          {NAV_TOP.filter(({ perms, superOnly }) => canSee(perms, superOnly)).map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              title={collapsed ? label : undefined}
              className={({ isActive }) => navCls(isActive)}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span className="text-sm">{label}</span>}
            </NavLink>
          ))}

          {/* ── Site Settings collapsible group ── */}
          {canSee(['settings.view']) && (collapsed ? (
            /* Icon-only mode: clicking navigates to settings */
            <button
              onClick={() => navigate('/admin/settings/content')}
              title="Site Settings"
              className={navCls(onSettingsRoute)}
            >
              <Settings className="w-4 h-4 flex-shrink-0" />
            </button>
          ) : (
            <div>
              {/* Group header */}
              <button
                onClick={() => setSettingsOpen(o => !o)}
                className={`flex items-center gap-3 w-full mx-3 py-2.5 rounded-lg text-sm border-l-[3px] rounded-l-none pl-[13px] transition-all duration-150
                  ${onSettingsRoute
                    ? 'bg-white/15 text-white font-semibold border-secondary'
                    : 'text-white/60 hover:text-white hover:bg-white/10 border-transparent'
                  }`}
                style={{ width: 'calc(100% - 1.5rem)' }}
              >
                <Settings className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm flex-1 text-left">Site Settings</span>
                <ChevronDown
                  className={`w-3.5 h-3.5 mr-2 transition-transform duration-200 ${settingsOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Sub-items */}
              {settingsOpen && (
                <div className="mt-0.5 mb-1">
                  {SETTINGS_ITEMS.map(({ tab, label, icon: Icon }) => (
                    <NavLink
                      key={tab}
                      to={`/admin/settings/${tab}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 py-2 pl-10 pr-3 mx-3 rounded-lg text-xs transition-all duration-150
                        ${isActive
                          ? 'bg-white/10 text-white font-medium'
                          : 'text-white/50 hover:text-white/80 hover:bg-white/8'
                        }`
                      }
                    >
                      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                      {label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Footer: user + logout */}
        <div className={`border-t border-white/10 ${collapsed ? 'py-4 flex flex-col items-center gap-3' : 'px-5 py-4'}`}>
          {!collapsed && (
            <p className="text-xs text-white/40 truncate mb-3">{user?.username || user?.email}</p>
          )}
          <button
            onClick={() => { logout(); navigate('/'); }}
            title={collapsed ? 'Logout' : undefined}
            className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {!collapsed && 'Logout'}
          </button>
        </div>
      </aside>

      {/* ── Main content area ─────────────────────────────────────────────── */}
      <main className={`flex-1 ${mainML} min-h-screen bg-muted transition-all duration-200`}>
        <Outlet />
      </main>
    </div>
  );
}
